// chat-dnd.ts — drag-and-drop of sidebar chats into folders (SAY-1820).
// A dragged chat row writes its id (and, when it is part of the current
// selection, every selected id) into the DataTransfer under a private MIME
// type, so only our own drop zones react — a file or a link dragged over the
// sidebar is ignored. Pure helpers; the components own the event wiring.

export const CHAT_DRAG_TYPE = 'application/x-sayano-chat-ids';

export function setDraggedChats(dt: DataTransfer | null, ids: string[]): void {
	if (!dt) return;
	dt.setData(CHAT_DRAG_TYPE, JSON.stringify(ids));
	dt.effectAllowed = 'move';
}

/** Whether a drag carries chat ids (readable during dragover without the payload). */
export function isChatDrag(dt: DataTransfer | null): boolean {
	return !!dt && Array.from(dt.types).includes(CHAT_DRAG_TYPE);
}

/** The dragged chat ids, or [] when the drop is not ours / is malformed. */
export function getDraggedChats(dt: DataTransfer | null): string[] {
	if (!dt) return [];
	try {
		const raw = dt.getData(CHAT_DRAG_TYPE);
		const arr = raw ? (JSON.parse(raw) as unknown) : [];
		return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string' && x.length > 0) : [];
	} catch {
		return [];
	}
}

/** The ids a drag of `id` should carry: the whole selection when `id` is in it. */
export function dragPayload(id: string, selected: Iterable<string>): string[] {
	const set = new Set(selected);
	return set.has(id) ? [...set] : [id];
}

/** Sort key: pinned chats first (most recently pinned on top), then recency. */
export function compareRows<T extends { sessionId: string; lastActivityAt: number }>(
	rank: (id: string) => number,
): (a: T, b: T) => number {
	return (a, b) => {
		const pa = rank(a.sessionId);
		const pb = rank(b.sessionId);
		if (pa !== pb) {
			if (pa === -1) return 1;
			if (pb === -1) return -1;
			return pb - pa;
		}
		return b.lastActivityAt - a.lastActivityAt;
	};
}
